import { Loader2, RotateCcw } from "lucide-react";
import { assetUrl, MSG } from "@/lib/api";
import { Disclosure } from "@/components/Provenance";
import { fmtElapsed, useElapsed } from "@/lib/hooks";

const STAGES = [["queued", "Queued"], ["generating", "Generating"], ["saving", "Saving"]];

const Progress = ({ job }) => {
  const running = ["queued", "generating", "saving"].includes(job.stage);
  const elapsed = useElapsed(job.createdAt, running);
  const idx = STAGES.findIndex(([k]) => k === job.stage);
  return (
    <div className="rounded-xl bg-[var(--charcoal)] p-5 text-[var(--bone)] sm:p-6" data-testid="walkthrough-progress">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-xl"><Loader2 size={17} className="animate-spin" /> Rendering walkthrough</div>
        <span className="font-mono-ui text-sm tabular-nums" data-testid="walkthrough-elapsed">{fmtElapsed(elapsed)}</span>
      </div>
      <ol className="mt-5 grid grid-cols-3 gap-2">
        {STAGES.map(([k, label], i) => (
          <li key={k} data-testid={`walkthrough-stage-${k}`} data-active={i === idx} className={`border-t-2 pt-2 text-xs uppercase tracking-[0.08em] transition-colors duration-500 ${i < idx ? "border-[var(--bone)]/70 text-[var(--bone)]/70" : i === idx ? "border-[var(--accent-soft)] text-[var(--bone)]" : "border-[var(--bone)]/15 text-[var(--bone)]/35"}`}>
            {label}{i === idx && <span className="pulse-dot ml-1">•</span>}
          </li>
        ))}
      </ol>
      {job.autoRetried && <p className="mt-4 text-xs text-[var(--bone)]/70" data-testid="walkthrough-auto-retry">First attempt failed — retrying automatically (attempt {job.attempt} of {job.maxAttempts}).</p>}
      <p className="mt-4 text-xs text-[var(--bone)]/60">You can leave this page. Rendering continues and progress resumes when you return.</p>
    </div>
  );
};

export const Walkthrough = ({ job, done, isOwner, canCreate, onCreate, creating }) => {
  const running = job && ["queued", "generating", "saving"].includes(job.stage);
  const failed = job && job.stage === "failed";
  return (
    <section data-testid="walkthrough-section">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow mb-2">Walkthrough</div>
          <h2 className="font-display text-3xl tracking-tight">A ten-second walk through the vision</h2>
        </div>
      </div>
      <p className="mb-5 text-xs text-[var(--stone-dark)]" data-testid="walkthrough-label">{MSG.walkLabel}</p>
      {done?.videoUrl && (
        <div className="mb-5">
          <div className="relative">
          <Disclosure testid="walkthrough-disclosure" />
          <video
            key={done.videoUrl}
            src={assetUrl(done.videoUrl)}
            poster={assetUrl(done.posterUrl)}
            controls
            playsInline
            muted
            loop
            preload="metadata"
            className="w-full rounded-xl bg-[var(--charcoal)]"
            data-testid="walkthrough-video-player"
          />
          </div>
          <div className="mt-2 font-mono-ui text-[0.7rem] text-[var(--stone-dark)]" data-testid="walkthrough-duration">
            Measured duration {done.measuredDuration?.toFixed(1)} s{done.continuationsUsed ? ` · ${done.continuationsUsed + 1} joined clips` : ""}
          </div>
        </div>
      )}
      {running && <Progress job={job} />}
      {failed && isOwner && (
        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-[#B42318]/30 bg-[#FDECEA] px-5 py-4" data-testid="walkthrough-failed">
          <span className="font-display text-xl">The walkthrough didn't render.</span>
          <button className="btn-primary" onClick={onCreate} disabled={creating} data-testid="walkthrough-retry-button"><RotateCcw size={15} /> Try again</button>
        </div>
      )}
      {isOwner && !running && !failed && (
        <button className={done ? "btn-ghost" : "btn-primary"} onClick={onCreate} disabled={!canCreate || creating} data-testid="create-walkthrough-button">
          {creating && <Loader2 size={15} className="animate-spin" />} {done ? "Create another walkthrough" : "Create walkthrough"}
        </button>
      )}
      {!isOwner && !done && <p className="text-sm text-[var(--stone-dark)]" data-testid="walkthrough-none">No walkthrough yet.</p>}
    </section>
  );
};
